import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getJournal, getNote } from "@/lib/api";
import EntryRenderer from "@/components/EntryRenderer";
import { ArrowLeft, Pencil, ChevronUp, ChevronDown } from "lucide-react";

function fmtDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

export default function EntryViewer({ space }) {
    const { id } = useParams();
    const nav = useNavigate();
    const [sp] = useSearchParams();
    const [data, setData] = useState(null);
    const [step, setStep] = useState(0);

    const fromPerson = sp.get("from") === "person";
    const subjectId = sp.get("subject");
    const jumpNodeId = sp.get("node");

    useEffect(() => {
        (async () => {
            const r = space === "journal" ? await getJournal(id) : await getNote(id);
            setData(space === "journal" ? r.entry : r.note);
        })();
    }, [id, space]);

    // Deep-link scroll + highlight
    useEffect(() => {
        if (!data || !fromPerson || !jumpNodeId) return;
        const el = document.getElementById(`node-${jumpNodeId}`);
        if (el) {
            setTimeout(() => {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.classList.add("mention-pulse");
                setTimeout(() => el.classList.remove("mention-pulse"), 1600);
            }, 100);
        }
        setStep(0);
    }, [data, fromPerson, jumpNodeId]);

    if (!data) return <div className="p-6 font-ui text-sm text-ink-40">Loading…</div>;

    // Person mentions for stepper
    const personMentions = fromPerson && subjectId
        ? (data.content || []).filter(n => n.type === "mention" && n.subject_id === subjectId)
        : [];

    const jumpTo = (i) => {
        const n = personMentions[i];
        if (!n) return;
        const el = document.getElementById(`node-${n.node_id}`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("mention-pulse");
            setTimeout(() => el.classList.remove("mention-pulse"), 1600);
        }
        setStep(i);
    };

    return (
        <div className="px-5 md:px-8 py-4 md:py-6 pb-32 mx-auto w-full relative" style={{ maxWidth: "68ch" }}>
            <div className="flex items-center justify-between mb-4">
                <button onClick={() => nav(fromPerson ? `/prayer/${subjectId}` : (space === "journal" ? "/journal" : "/notes"))}
                    className="inline-flex items-center gap-1.5 font-ui text-[13px] text-ink-70 hover:text-ink">
                    <ArrowLeft size={14} /> Back
                </button>
                <button
                    data-testid="edit-entry"
                    onClick={() => nav(`/${space}/${id}/edit`)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-ink-10 font-ui text-[13px] hover:border-moss/40 transition"
                >
                    <Pencil size={14} /> Edit
                </button>
            </div>

            {space === "journal" ? (
                <div className="font-ui text-[11px] uppercase tracking-[0.2em] text-moss-deep font-semibold mb-2">
                    {fmtDate(data.date)}
                </div>
            ) : (
                <>
                    <h1 className="scripture text-3xl md:text-4xl text-ink tracking-tight mb-2">
                        {data.title || <span className="text-ink-40 italic">Untitled</span>}
                    </h1>
                    {data.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                            {data.tags.map(t => (
                                <span key={t} className="px-2 py-0.5 rounded bg-sage-paper text-ink-70 font-ui text-[11px]">#{t}</span>
                            ))}
                        </div>
                    )}
                </>
            )}

            <EntryRenderer content={data.content} highlightMentionId={jumpNodeId} />

            {personMentions.length > 1 && (
                <div
                    data-testid="mention-stepper"
                    className="fixed bottom-24 right-4 lg:right-10 z-30 bg-white/95 backdrop-blur border border-plum/30 rounded-full shadow-md flex items-center gap-1 px-2 py-1"
                >
                    <button
                        data-testid="stepper-prev"
                        onClick={() => jumpTo((step - 1 + personMentions.length) % personMentions.length)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-plum hover:bg-plum/10"
                        aria-label="Previous mention"
                    >
                        <ChevronUp size={14} />
                    </button>
                    <span data-testid="stepper-label" className="font-ui text-[11px] font-semibold text-plum uppercase tracking-[0.12em] px-1">
                        {step + 1} of {personMentions.length}
                    </span>
                    <button
                        data-testid="stepper-next"
                        onClick={() => jumpTo((step + 1) % personMentions.length)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-plum hover:bg-plum/10"
                        aria-label="Next mention"
                    >
                        <ChevronDown size={14} />
                    </button>
                </div>
            )}
        </div>
    );
}
